import * as Yup from 'yup';

export const loginSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().min(6, 'Must be at least 6 characters').required('Password is required'),
});

export const registerSchema = Yup.object({
  firstName: Yup.string().min(2, 'Minimum 2 characters').required('First name is required'),
  lastName: Yup.string().min(2, 'Minimum 2 characters').required('Last name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .min(8, 'Must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain an uppercase letter')
    .matches(/[a-z]/, 'Must contain a lowercase letter')
    .matches(/[0-9]/, 'Must contain a number')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

export const forgotSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
});

/**
 * The password policy (TASK-2026-10-10-001, raised to 12 characters by
 * TASK-2026-10-12-002): 12–64 characters, an uppercase letter, a lowercase
 * letter, a number and a special character, no spaces. Applied to Change
 * Password and to the password an admin sets on Add User.
 *
 * The backend enforces the same regex in src/utils/passwordPolicy.js. The
 * duplication is deliberate — the two projects share no code — so change both
 * together.
 */
export const PASSWORD_POLICY_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])\S{12,64}$/;

/** The policy split into the rules the password checklists show. */
export const PASSWORD_RULES = [
  { id: 'length', label: '12 to 64 characters', test: (v) => v.length >= 12 && v.length <= 64 },
  { id: 'upper', label: 'An uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { id: 'lower', label: 'A lowercase letter', test: (v) => /[a-z]/.test(v) },
  { id: 'digit', label: 'A number', test: (v) => /\d/.test(v) },
  { id: 'special', label: 'A special character', test: (v) => /[^A-Za-z0-9\s]/.test(v) },
  { id: 'spaces', label: 'No spaces', test: (v) => v.length > 0 && !/\s/.test(v) },
];

export const otpSchema = Yup.object({
  otp: Yup.string()
    .trim()
    .required('OTP is required')
    .matches(/^\d{6}$/, 'Enter the 6-digit code from the email'),
});

export const resetPasswordSchema = Yup.object({
  newPassword: Yup.string()
    .required('New password is required')
    .matches(PASSWORD_POLICY_REGEX, 'Password does not meet the requirements below'),
  confirmPassword: Yup.string()
    .required('Confirm your new password')
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match'),
});

/**
 * Change Password from the account menu (TASK-2026-10-12-002). The backend's
 * changePasswordValidator enforces the same rules; whether the old password is
 * right is only known to the server.
 */
export const changePasswordSchema = Yup.object({
  oldPassword: Yup.string().required('Old password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .matches(PASSWORD_POLICY_REGEX, 'Password does not meet the requirements below'),
  confirmPassword: Yup.string()
    .required('Confirm your new password')
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match'),
});
